import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faMagnifyingGlassPlus } from '@fortawesome/free-solid-svg-icons';

import useDeepCompareRef from 'Hooks/useDeepCompareRef';
import ImageSrcSizes from 'Components/Carousel/ProductMediaCarousel/ImageSrcSizes';
import useViewport from 'Hooks/useViewport';
import ProductCover from 'Components/Utility/ProductCover';
import FluidImg from 'Components/Utility/FluidImg';
import pauseYTVideos from 'Helpers/Video/VideoHelpers';
import TrustPilotCarouselBanner from 'Components/Carousel/TrustPilotCarouselBanner/TrustPilotCarouselBanner';
import ProductMediaModal from 'Components/Carousel/ProductMediaCarousel/ProductMediaModal/ProductMediaModal';
import SliderVideo from 'Components/Carousel/ProductMediaCarousel/SliderVideo';
import AgeGatingContainer from 'Components/Utility/Age/AgeGatingContainer';
import useSlideBuilder from 'Components/Carousel/ProductMediaCarousel/hooks/useSlideBuilder';
import useAgeGateRequired from 'Components/Carousel/ProductMediaCarousel/hooks/useAgeGateRequired';
import useCarouselSettings from 'Components/Carousel/ProductMediaCarousel/hooks/useCarouselSettings';

import 'Components/Carousel/ProductMediaCarousel/ProductMediaCarousel.scss';

const ProductMediaCarousel = ({
  cover,
  coverAltText,
  name,
  displayType,
  coverType,
  sizesOverride,
  screenshots,
  videos,
  bundleImages,
  bundleVideos,
  passedAgeGate,
  ageRatings,
  unlinkCoverFromVideo,
  dateOfBirth,
  carouselType,
  hideTrustPilot,
  sliderSettings,
}) => {
  const [maxModalOpen, setMaxModalOpen] = useState(false);
  const [currentScreenshotNumber, setCurrentScreenshotNumber] = useState(1);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [trackPosition, setTrackPosition] = useState(0);
  const [activeGameNumber, setActiveGameNumber] = useState(0);
  const [interacted, setInteracted] = useState(false);

  const sliderRef = useRef(null);
  const sliderTrackRef = useRef(null);

  const { currentBreakpoint, width, isBelowLg, isAboveMd } = useViewport();

  // Reset Carousel if media content actually changes (deep comparison)
  const stableScreenshots = useDeepCompareRef(screenshots);
  const stableVideos = useDeepCompareRef(videos);
  const stableBundleImages = useDeepCompareRef(bundleImages);
  const stableBundleVideos = useDeepCompareRef(bundleVideos);

  // Reset Carousel position when any media content changes
  useEffect(() => {
    setSliderPosition(0);
    setActiveGameNumber(0);
    setTrackPosition(0);
  }, [stableScreenshots, stableVideos, stableBundleImages, stableBundleVideos]);

  const ageGateRequired = useAgeGateRequired(passedAgeGate, ageRatings, dateOfBirth);

  const {
    trackSlidesToShow,
    sliderWidth,
    trackGap,
    trackSlideWidth,
    defaultSettings,
  } = useCarouselSettings(sliderSettings, currentBreakpoint, width, isBelowLg);

  const slides = useSlideBuilder({
    cover,
    coverAltText,
    name,
    videos,
    bundleVideos,
    bundleImages,
    screenshots,
    ageGateRequired,
    unlinkCoverFromVideo,
  });

  const totalScreenshots = slides.filter(slide => slide.screenshotNumber);

  const noMedia = isEmpty(screenshots) && isEmpty(videos) && isEmpty(bundleImages) && isEmpty(bundleVideos); // eslint-disable-line max-len

  if (noMedia) {
    return (
      <div className={classNames('ProductMediaCarousel ProductMediaCarousel--singleCover')}>
        <ProductCover
          cover={cover}
          alt={coverAltText || `Product cover for ${name}`}
          sizesOverride={sizesOverride}
          displayType={displayType}
          coverType={coverType}
        />
        {!hideTrustPilot &&
          <TrustPilotCarouselBanner />
        }
      </div>
    );
  }

  const viewScreenshotModal = (screenshotNumber) => {
    setMaxModalOpen(true);
    setCurrentScreenshotNumber(screenshotNumber);
  };

  const changeScreenshot = (direction) => {
    if (direction === 'previous') setCurrentScreenshotNumber(currentScreenshotNumber - 1);
    if (direction === 'next') setCurrentScreenshotNumber(currentScreenshotNumber + 1);
  };

  const clickTrackItem = (number) => {
    // On desktop, we want to move the track to the left so that the clicked item is in view.
    if (isAboveMd && slides.length > trackSlidesToShow) {
      setTrackPosition(-(number) * (trackSlideWidth + trackGap));
    }

    // When clicking a track item, we want to set the slider position to the clicked item's position.
    // This is to ensure the main slider shows the correct item.
    // We also set the active game number to the clicked item's number.
    setSliderPosition(-(sliderWidth * number));
    setActiveGameNumber(number);

    if (!interacted) {
      setInteracted(true);
    }

    // Pause any YouTube videos when clicking a track item.
    pauseYTVideos();
  };

  const loadTrackNext = () => {
    // Calculate the total movement of the track based on the number of slides to show and the gap between them.
    const totalMovement = (trackSlideWidth + trackGap) * trackSlidesToShow;

    // The track length is the width of each slide multiplied by the number of slides minus one.
    // We subtract one slide because the track starts at 0.
    // trackLength is negative because the track moves to the left.
    const trackLength = -(trackSlideWidth + trackGap) * (slides.length - 1);

    // If clicking next would take the track position past the end of the track, we set it to the end.
    // This is to prevent the track from moving beyond the last slide.
    if ((trackPosition - totalMovement) < trackLength) {
      setTrackPosition(trackLength);
    } else {
      setTrackPosition(trackPosition - totalMovement);
    }

    if (!interacted) {
      setInteracted(true);
    }
  };

  const loadTrackPrevious = () => {
    // Calculate the total movement of the track based on the number of slides to show and the gap between them.
    const totalMovement = (trackSlideWidth + trackGap) * trackSlidesToShow;

    // If clicking previous would take the track position above 0, we set it to 0.
    // This is to prevent the track from moving beyond the first slide.
    if ((trackPosition + totalMovement) > 0) {
      setTrackPosition(0);
    } else {
      setTrackPosition(trackPosition + totalMovement);
    }

    if (!interacted) {
      setInteracted(true);
    }
  };

  const loadNext = () => {
    if (activeGameNumber + 1 === slides.length) {
      setSliderPosition(0);
      setActiveGameNumber(0);
    } else {
      setSliderPosition(sliderPosition - sliderWidth);
      setActiveGameNumber(activeGameNumber + 1);
    }

    // Move track on desktop
    if (isAboveMd && slides.length > trackSlidesToShow) {
      if (activeGameNumber + 1 === slides.length) {
        setTrackPosition(0);
      } else {
        setTrackPosition(-(activeGameNumber + 1) * (trackSlideWidth + trackGap));
      }
    }

    if (!interacted) {
      setInteracted(true);
    }

    // Pause any YouTube videos when clicking next.
    pauseYTVideos();
  };

  const loadPrevious = () => {
    if (activeGameNumber === 0) {
      setSliderPosition(sliderPosition - (sliderWidth * (slides.length - 1)));
      setActiveGameNumber(slides.length - 1);
    } else {
      setSliderPosition(sliderPosition + sliderWidth);
      setActiveGameNumber(activeGameNumber - 1);
    }

    // Move track on desktop
    if (isAboveMd && slides.length > trackSlidesToShow) {
      if (activeGameNumber === 0) {
        setTrackPosition(-(slides.length - 1) * (trackSlideWidth + trackGap));
      } else {
        setTrackPosition(-(activeGameNumber - 1) * (trackSlideWidth + trackGap));
      }
    }

    if (!interacted) {
      setInteracted(true);
    }

    // Pause any YouTube videos when clicking previous.
    pauseYTVideos();
  };

  // If use scrolls on a touch device, set interacted to true so that the slides render
  const handleScroll = () => {
    if (isBelowLg && !interacted) {
      setInteracted(true);
    }
  };

  return (
    <React.Fragment>
      <div className={classNames('ProductMediaCarousel ProductMediaCarousel--includesTrustPilot', { 'ProductMediaCarousel--singleCover': slides.length === 1 })}>
        <div className="ProductMediaCarousel__main" ref={sliderRef}>
          <ul className="ProductMediaCarousel__main__slides" style={{ left: `${sliderPosition}px` }}>
            {slides.map((slide, i) => {
              if (slide.type === 'cover') {
                return (
                  <li
                    key={slide.number}
                    className="ProductMediaCarousel__main__slides__slide"
                    style={{ width: `${sliderWidth}px` }}
                    aria-hidden={i !== activeGameNumber}
                  >
                    {(i === 0 || interacted) &&
                      <ProductCover
                        cover={slide.image}
                        alt={coverAltText || `Product cover for ${name}`}
                        sizesOverride={sizesOverride}
                        displayType={displayType}
                        coverType={coverType}
                      />
                    }
                  </li>
                );
              }

              if (slide.type === 'video' || slide.type === 'bundle-video') {
                return (
                  <li
                    key={slide.number}
                    className="ProductMediaCarousel__main__slides__slide"
                    style={{ width: `${sliderWidth}px` }}
                    aria-hidden={i !== activeGameNumber}
                  >
                    {(i === 0 || interacted) &&
                      <SliderVideo
                        cover={slide.image}
                        alt={slide.alt}
                        src={slide.video}
                        ageGateRequired={ageGateRequired}
                        ytCover={slide.ytCover}
                        sizesOverride={sizesOverride}
                        displayType={displayType}
                        coverType={coverType}
                      />
                    }
                  </li>
                );
              }

              return (
                <li
                  key={slide.number}
                  className={classNames('ProductMediaCarousel__main__slides__slide', { 'ProductMediaCarousel__main__slides__slide--ageGated': ageGateRequired })}
                  style={{ width: `${sliderWidth}px` }}
                  aria-hidden={i !== activeGameNumber}
                >
                  <div className="responsive-image-island">
                    <div className="responsive-image responsive-image--16by9">
                      {(i === 0 || interacted) &&
                        <FluidImg
                          ratio16by9={carouselType !== 'non game'}
                          className={classNames('img-force-full slide-image', { 'cover-image-foreground': carouselType === 'non game' })}
                          src={slide.image}
                          alt={slide.alt}
                          sizesOverride={sizesOverride}
                          lazy={i > 0}
                        />
                      }
                      {(i === 0 || interacted) && carouselType === 'non game' &&
                        <FluidImg
                          className="cover-image-background"
                          src={slide.image}
                          alt="Blurred background image"
                          sizesOverride={sizesOverride}
                          lazy={i > 0}
                        />
                      }
                    </div>
                  </div>

                  {ageGateRequired &&
                    <AgeGatingContainer ratings={ageRatings} lazyLoad={false} className="ProductMediaCarousel__main__slide__ageGating" />
                  }

                  {(i === 0 || interacted) && slide.type === 'image' && slide.screenshotNumber && !ageGateRequired &&
                    <button className="ProductMediaCarousel__main__maximiseButton" onClick={() => { viewScreenshotModal(slide.screenshotNumber); }}>
                      <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                    </button>
                  }
                </li>
              );
            })}
          </ul>

          {slides.length > 1 &&
            <>
              <div className="ProductMediaCarousel__main__leftArrow">
                <button
                  className="ProductMediaCarousel__main__leftArrow__button"
                  onClick={() => loadPrevious()}
                >
                  <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
                </button>
              </div>
              <div className="ProductMediaCarousel__main__rightArrow">
                <button
                  className="ProductMediaCarousel__main__rightArrow__button"
                  onClick={() => loadNext()}
                >
                  <FontAwesomeIcon icon={faChevronRight} title="Next" />
                </button>
              </div>
            </>
          }
        </div>

        {!hideTrustPilot &&
          <TrustPilotCarouselBanner />
        }

        {slides.length > 1 &&
          <div
            ref={sliderTrackRef}
            className={classNames('ProductMediaCarousel__track', { 'ProductMediaCarousel__track--ageGated': ageGateRequired })}
          >
            <div className="ProductMediaCarousel__track__slides" style={{ left: `${trackPosition}px`, gap: `${trackGap}px` }} onScroll={handleScroll}>
              {slides.map((slide, i) => {
                // Track full length is negative because the track moves to the left from a start of zero.
                const visibleTrack = trackPosition - ((trackSlideWidth + trackGap) * trackSlidesToShow); // eslint-disable-line max-len
                const slidePosition = 0 - (i * (trackSlideWidth + trackGap));
                const isSlideVisible = (slidePosition > visibleTrack) && (slidePosition <= trackPosition); // eslint-disable-line max-len

                if (isAboveMd && i + 1 > trackSlidesToShow && !interacted) return false;
                if (isBelowLg && i + 1 > trackSlidesToShow + 1 && !interacted) return false;

                if (slide.type === 'cover' || slide.type === 'bundle-video') {
                  return (
                    <button
                      className={classNames('ProductMediaCarousel__track__slides__slide', { 'ProductMediaCarousel__track__slides__slide--active': activeGameNumber === i })}
                      style={{ width: `${trackSlideWidth}px` }}
                      aria-hidden={!isSlideVisible}
                      tabIndex={0}
                      key={slide.number}
                      onClick={() => { clickTrackItem(i); }}
                    >
                      {(i <= trackSlidesToShow || interacted) &&
                        <ProductCover
                          cover={slide.image}
                          alt={slide.alt}
                          sizesOverride={ImageSrcSizes(sliderSettings, defaultSettings)}
                          displayType={displayType}
                          coverType={coverType}
                          key={slide.number}
                        />
                      }
                    </button>
                  );
                }

                return (
                  <button
                    key={slide.number}
                    aria-label="View image"
                    className={classNames('ProductMediaCarousel__track__slides__slide', { 'ProductMediaCarousel__track__slides__slide--active': activeGameNumber === i })}
                    style={{ width: `${trackSlideWidth}px` }}
                    aria-hidden={!isSlideVisible}
                    tabIndex={0}
                    onClick={() => { clickTrackItem(i); }}
                  >
                    <div className="responsive-image-island">
                      <div className="responsive-image responsive-image--16by9">
                        {(i <= trackSlidesToShow || interacted) &&
                          <FluidImg
                            ratio16by9={carouselType !== 'non game'}
                            className={classNames('img-force-full slide-image', { 'cover-image-foreground': carouselType === 'non game' })}
                            youtubeId={slide.ytCover ? slide.video : null}
                            src={slide.image}
                            alt={slide.alt}
                            sizesOverride={ImageSrcSizes(sliderSettings, defaultSettings)}
                          />
                        }
                        {(i <= trackSlidesToShow || interacted) && carouselType === 'non game' &&
                          <FluidImg
                            className="cover-image-background"
                            youtubeId={slide.ytCover ? slide.video : null}
                            src={slide.image}
                            alt="Blurred background image"
                            sizesOverride={ImageSrcSizes(sliderSettings, defaultSettings)}
                          />
                        }
                      </div>
                    </div>

                    {slide.type === 'video' &&
                      <div className="ProductMediaCarousel__track__slide__buttonContainer">
                        <img src="https://cdn.fanatical.com/production/icons/youtube-icon-white.svg" alt="YouTube" loading="lazy" className="ProductMediaCarousel__track__slide__buttonContainer__img" />
                      </div>
                    }
                  </button>
                );
              })}
            </div>

            {isAboveMd && slides.length > trackSlidesToShow &&
              <>
                <button
                  className="ProductMediaCarousel__track__leftArrow"
                  disabled={trackPosition === 0}
                  onClick={() => loadTrackPrevious()}
                >
                  <FontAwesomeIcon icon={faChevronLeft} title="Previous" />
                </button>
                <button
                  className="ProductMediaCarousel__track__rightArrow"
                  disabled={trackPosition === -(trackSlideWidth + trackGap) * (slides.length - 1)}
                  onClick={() => loadTrackNext()}
                >
                  <FontAwesomeIcon icon={faChevronRight} title="Next" />
                </button>
              </>
            }
          </div>
        }
      </div>

      {!isEmpty(totalScreenshots) &&
        <ProductMediaModal
          isOpen={maxModalOpen}
          screenshots={totalScreenshots}
          currentScreenshotNumber={currentScreenshotNumber}
          carouselType={carouselType}
          onClose={setMaxModalOpen}
          onNext={() => changeScreenshot('next')}
          onPrevious={() => changeScreenshot('previous')}
        />
      }
    </React.Fragment>
  );
};

ProductMediaCarousel.propTypes = {
  cover: PropTypes.string,
  coverAltText: PropTypes.string,
  coverType: PropTypes.string,
  displayType: PropTypes.string,
  name: PropTypes.string,
  sizesOverride: PropTypes.string,
  screenshots: PropTypes.arrayOf(PropTypes.object),
  videos: PropTypes.arrayOf(PropTypes.object),
  bundleImages: PropTypes.arrayOf(PropTypes.object),
  bundleVideos: PropTypes.arrayOf(PropTypes.object),
  ageRatings: PropTypes.object,
  passedAgeGate: PropTypes.bool.isRequired,
  unlinkCoverFromVideo: PropTypes.bool,
  dateOfBirth: PropTypes.string,
  carouselType: PropTypes.string,
  hideTrustPilot: PropTypes.bool,
  sliderSettings: PropTypes.shape({}),
};

ProductMediaCarousel.defaultProps = {
  cover: '',
  coverAltText: '',
  coverType: '',
  displayType: '',
  name: '',
  sizesOverride: '',
  screenshots: [],
  videos: [],
  bundleImages: [],
  bundleVideos: [],
  ageRatings: {},
  unlinkCoverFromVideo: false,
  dateOfBirth: '',
  carouselType: '',
  hideTrustPilot: false,
  sliderSettings: {},
};

const mapStateToProps = ({
  ageGating: { success },
  auth: { dob },
}) => ({
  passedAgeGate: success,
  dateOfBirth: dob,
});

export default connect(mapStateToProps)(ProductMediaCarousel);
